import React from "react";

/* ─────────────────────────────────────────────────────────────────────
   RootErrorBoundary
   ─────────────────────────────────────────────────────────────────────
   The single most important guard against "silent white page". If ANY
   error is thrown while rendering the tree — a poisoned redux-persist
   state, a component that throws on a restored tab, or (very common after
   a deploy) a code-split chunk that 404s because the old bundle was
   replaced — React unmounts the whole tree and you get a blank page with
   no message. This boundary catches that and does something useful.

   Two behaviours:

   1. CHUNK-LOAD errors (stale tab requesting a chunk that a new deploy
      removed) → reload ONCE automatically. A fresh load pulls the new
      index.html (nginx sends no-store) and the new chunk hashes. The
      one-shot guard in sessionStorage prevents a reload loop.

   2. ANY OTHER error → show a small recovery card with a "Reset & reload"
      button that wipes persisted state + caches + service workers, so the
      user can self-recover without being told to "clear your cache".
   ───────────────────────────────────────────────────────────────────── */

const RELOAD_GUARD = "serp-chunk-reload";

function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || "";
  const msg = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /import\(\) failed/i.test(msg)
  );
}

async function hardReset() {
  // Wipe persisted redux state (any key), service workers, and caches.
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("persist:"))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* ignore */ }

  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) { /* ignore */ }

  try {
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) { /* ignore */ }

  // Cache-busting reload.
  window.location.replace(
    window.location.pathname + "?_r=" + Date.now()
  );
}

class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunk: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunk: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    // Log for your own diagnostics (shows up in the console / any logger).
    // eslint-disable-next-line no-console
    console.error("[RootErrorBoundary]", error, info);

    if (isChunkLoadError(error)) {
      // A stale tab asked for a chunk a deploy removed. Reload ONCE.
      const already = sessionStorage.getItem(RELOAD_GUARD);
      if (!already) {
        sessionStorage.setItem(RELOAD_GUARD, "1");
        window.location.reload();
        return;
      }
      // If a single reload didn't fix it, fall through to the hard reset UI.
    }
  }

  componentDidMount() {
    // If we mounted successfully, the app is healthy — clear the guard so a
    // future chunk error is allowed to auto-reload again.
    try { sessionStorage.removeItem(RELOAD_GUARD); } catch (e) { /* ignore */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Chunk error mid-auto-reload: show a neutral "updating" splash so the
    // brief moment before reload isn't a scary blank page.
    if (this.state.isChunk && !sessionStorage.getItem(RELOAD_GUARD + "-shown")) {
      return (
        <div style={styles.wrap}>
          <div className="spinner-border" style={{ color: "var(--ds-primary, #1e40af)" }} />
          <div style={styles.msg}>Updating to the latest version…</div>
        </div>
      );
    }

    // Generic recovery card.
    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <i className="bi bi-arrow-clockwise" style={styles.icon} />
          <h5 style={styles.title}>Something needs a refresh</h5>
          <p style={styles.body}>
            The app hit a snag loading. This usually clears itself with a reset —
            you won't lose any saved data on the server.
          </p>
          <button style={styles.btn} onClick={hardReset}>
            Reset &amp; reload
          </button>
        </div>
      </div>
    );
  }
}

const styles = {
  wrap: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
    background: "var(--ds-bg, #f8fafc)",
    fontFamily: "var(--ds-font-family, system-ui, sans-serif)",
  },
  msg: { color: "var(--ds-text-muted, #64748b)", fontSize: "0.9rem" },
  card: {
    maxWidth: 380,
    textAlign: "center",
    padding: "2rem",
    background: "var(--ds-surface, #fff)",
    border: "1px solid var(--ds-border, #e2e8f0)",
    borderRadius: "var(--ds-radius-lg, 12px)",
    boxShadow: "var(--ds-shadow-md, 0 4px 12px rgba(0,0,0,.08))",
  },
  icon: { fontSize: "2rem", color: "var(--ds-primary, #1e40af)" },
  title: { marginTop: "0.75rem", color: "var(--ds-text, #0f172a)" },
  body: { color: "var(--ds-text-muted, #64748b)", fontSize: "0.9rem", margin: "0.5rem 0 1.25rem" },
  btn: {
    border: "none",
    borderRadius: "var(--ds-radius-md, 8px)",
    padding: "0.6rem 1.4rem",
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#fff",
    background: "var(--ds-primary, #1e40af)",
    cursor: "pointer",
  },
};

export default RootErrorBoundary;
